import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  computed,
  effect,
  inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  signal,
  TransferState,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { finalize, map, Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { AuthService } from '../../core/services/auth/auth.service';
import { StorageKeys, StorageService } from '../../core/services/storage/storage.service';
import { Currency, Language } from '../../interfaces/cached-content';
import { Subscriber } from '../../interfaces/subscriber';
import { IdentityVerificationService } from '../../services/identity-verification/identity-verification.service';
import { distinctAuthenticatedState } from '../../utils/auth-distinct.pipe';
import { ApiConfigService } from '../api-config/api-config.service';
import { CacheService, DEFAULT_CURRENCY, DEFAULT_LANGUAGE } from '../cache/cache.service';

const SUBSCRIBER_STATE_KEY = makeStateKey<Subscriber>('SUBSCRIBER_CONTENT_STATE_KEY');

@Injectable({
  providedIn: 'root',
})
export class SubscriberService {
  protected platform = inject(PLATFORM_ID);

  protected cacheService = inject(CacheService);

  protected transferState = inject(TransferState);

  protected storageService = inject(StorageService);

  public subscriber = signal<Subscriber>(
    isPlatformBrowser(this.platform)
      ? this.transferState.get(SUBSCRIBER_STATE_KEY, this.defaultGuestSubscriber())
      : this.defaultGuestSubscriber()
  );

  public isPasswordSet = computed(() => this.subscriber().isPasswordSet);

  protected apiConfig = inject(ApiConfigService);

  protected http = inject(HttpClient);

  protected auth = inject(AuthService);

  private subscriberAutoLoadSub?: Subscription;

  protected identityVerificationService = inject(IdentityVerificationService);

  public isFetching = signal(false);

  /**
   * Returns the subscriber language
   */
  public subscriberLanguage = computed<Language>(() => {
    const subscriber = this.subscriber();
    const cacheContent = this.cacheService.cachedContent();

    if (!cacheContent) {
      return DEFAULT_LANGUAGE;
    }

    if (!cacheContent.cache.config.multiLingualMasterEnabled) {
      const fallbackLanguage = DEFAULT_LANGUAGE.code;
      return (
        cacheContent.cache.config.languages.supported.find(
          (value) => value.code === fallbackLanguage
        ) ?? DEFAULT_LANGUAGE
      );
    }

    // Fallback to English when not specified (e.g. in flag/config)
    const languageCode =
      subscriber.language || cacheContent.cache.config.languages.default || DEFAULT_LANGUAGE.code;
    const foundLanguage = cacheContent.cache.config.languages.supported.find(
      (value) => value.code === languageCode
    );
    return foundLanguage ?? DEFAULT_LANGUAGE;
  });

  /**
   * Returns the subscriber currency
   */
  public subscriberCurrency = computed<Currency>(() => {
    const subscriber = this.subscriber();
    const cachedContent = this.cacheService.cachedContent();

    if (!cachedContent) {
      return DEFAULT_CURRENCY;
    }

    if (
      !cachedContent.cache.config.currencyConversionMasterEnabled &&
      cachedContent.cache.config.currency
    ) {
      return this.cacheService.defaultCurrency();
    }

    // For logged-out users only, try to get currency from localStorage
    if (!this.auth.isAuthenticated()) {
      const selectedCurrencyFromStorage = this.storageService.getKey<
        Pick<Currency, 'currency' | 'currency_name'>
      >(StorageKeys.SelectedCurrency);

      if (selectedCurrencyFromStorage) {
        const foundFromStorage = cachedContent.cache.config.currencyObj.currencyList.find(
          (value) =>
            value.currency_name === selectedCurrencyFromStorage.currency_name ||
            value.currency === selectedCurrencyFromStorage.currency
        );

        if (foundFromStorage) {
          return foundFromStorage;
        }
      }
    }

    // Use subscriber's currency (works for both logged-in and logged-out)
    if (!subscriber.currencyObj?.currency) {
      return this.cacheService.defaultCurrency();
    }

    const foundFromSubscriber = cachedContent.cache.config.currencyObj.currencyList.find(
      (value) => value.currency === subscriber.currencyObj.currency
    );
    return foundFromSubscriber ?? this.cacheService.defaultCurrency();
  });

  protected isAuthenticated = toObservable(this.auth.isAuthenticated);

  constructor() {
    effect(() => {
      const isAuthenticated = this.auth.isAuthenticated();

      if (isPlatformServer(this.platform)) {
        return;
      }

      if (isAuthenticated) {
        return;
      }

      this.subscriber.set(this.defaultGuestSubscriber());
    });

    if (isPlatformServer(this.platform)) {
      // this is done as first time initialization the cache will give us default value then once setup is done
      // it will give us the actual default value

      effect(() => {
        const cacheContent = this.cacheService.cachedContent();

        if (cacheContent) {
          this.subscriber.set(this.defaultGuestSubscriber());
        }
      });
    }
  }

  public initAutoLoadSubscriber() {
    if (this.subscriberAutoLoadSub && !this.subscriberAutoLoadSub.closed) {
      return;
    }
    this.subscriberAutoLoadSub = distinctAuthenticatedState(this.isAuthenticated)
      .pipe(
        tap({
          next: () => this.initSubscriberFromStorage(),
        })
      )
      .pipe(switchMap(() => this.fetchSubscriber()))
      .subscribe();
  }

  public initSubscriberFromStorage(): void {
    const subscriber = this.storageService.getKey<Subscriber>(StorageKeys.Subscriber);

    if (subscriber) {
      this.subscriber.set(subscriber);
    }
  }

  /**
   * Fetches subscriber information
   * @returns
   */
  public fetchSubscriber(): Observable<Subscriber> {
    this.isFetching.set(true);
    return this.http.get<Subscriber>(this.apiConfig.url('subscribers/me')).pipe(
      map((result) => {
        this.subscriber.set(result);
        this.identityVerificationService.updateEkycDetails({
          status: result?.eKycStatus,
          sessionId: result?.eKycSessionId ?? undefined,
          sessionToken: result?.eKycSessionToken ?? undefined,
          sessionUrl: result?.eKycSessionUrl ?? undefined,
        });
        return result;
      }),
      finalize(() => this.isFetching.set(false))
    );
  }

  /**
   * Updates the currency of the subscriber
   * @param data
   * @returns
   */
  public updateCurrency(
    data: Pick<Currency, 'currency' | 'currency_name'>
  ): Observable<{ message: string }> {
    if (!this.auth.isAuthenticated()) {
      this.updateCurrencyState(data);
      return of({ message: 'OK' });
    }

    return this.http
      .put<{ message: string }>(this.apiConfig.url('subscribers/settings/currency'), data)
      .pipe(
        map((result) => {
          this.updateCurrencyState(data);
          return result;
        })
      );
  }

  /**
   * Updates the currency in currency state
   * @param currency
   */
  private updateCurrencyState(currency: Pick<Currency, 'currency' | 'currency_name'>): void {
    this.subscriber.update((subscriber) => ({
      ...subscriber,
      currencyObj: currency,
    }));

    // Save to localStorage only for logged-out users
    if (isPlatformBrowser(this.platform) && !this.auth.isAuthenticated()) {
      this.storageService.setKey(StorageKeys.SelectedCurrency, {
        currency: currency.currency,
        currency_name: currency.currency_name,
      });
    }
  }

  /**
   * Updates the subscriber language
   * @param language
   * @returns
   */
  public updateLanguage(language: string): Observable<{ message: string }> {
    if (!this.auth.isAuthenticated()) {
      this.updateLanguageState(language);
      return of({ message: 'OK' });
    }

    return this.http
      .post<{ message: string }>(this.apiConfig.url('subscribers/settings/language'), { language })
      .pipe(
        map((result) => {
          this.updateLanguageState(language);
          return result;
        })
      );
  }

  /**
   * Updates the language in subscriber state
   * @param language
   */
  private updateLanguageState(language: string): void {
    this.subscriber.update((subscriber) => ({
      ...subscriber,
      language,
    }));

    // Save to localStorage only for logged-out users
    if (isPlatformBrowser(this.platform) && !this.auth.isAuthenticated()) {
      this.storageService.setKey(StorageKeys.SelectedLanguage, language);
    }
  }

  /**
   * Generates a default guest subscriber
   * @returns
   */
  public defaultGuestSubscriber(): Subscriber {
    const defaultLanguage = this.cacheService.defaultLanguage();
    let defaultCurrency;

    if (isPlatformBrowser(this.platform) && this.cacheService.currencyConversionMasterEnabled()) {
      const selectedCurrencyFromStorage = this.storageService.getKey<
        Pick<Currency, 'currency' | 'currency_name'>
      >(StorageKeys.SelectedCurrency);

      defaultCurrency = selectedCurrencyFromStorage ?? this.cacheService.defaultCurrency();
    } else {
      defaultCurrency = this.cacheService.defaultCurrency();
    }

    return {
      _id: '-1',
      firstName: '',
      lastName: '',
      language:
        this.storageService.getKey<string>(StorageKeys.SelectedLanguage) ?? defaultLanguage.code,
      currencyObj: {
        currency: defaultCurrency.currency,
        currency_name: defaultCurrency.currency_name,
      },
      isGuest: true,
    } as never as Subscriber;
  }

  /**
   * Updates the subscriber
   * @param data
   * @returns
   */
  public updateSubscriber(data: { firstName: string; lastName: string }): Observable<Subscriber> {
    return this.http
      .put<Subscriber>(this.apiConfig.url('subscribers'), data)
      .pipe(switchMap(() => this.fetchSubscriber()));
  }
}
